import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="contacts--persons-disqualify"
//
// Orchestrates the Contacts (Persons) "Disqualify contact" modal (CONEK-5716).
// Mounted on the page container that wraps BOTH the list (inside the
// `contacts_list` Turbo Frame) and the modal (rendered once, outside the frame),
// so row triggers and the modal share one Stimulus scope and the wiring survives
// filter/sort/page frame swaps.
//
// Row buttons carry the contact's id/name/company plus its per-contact disqualify
// path (member route POST /contacts/:id/disqualify) as data-* params and
// `data-action="click->contacts--persons-disqualify#open"`. open() sets the modal
// form's action to that path, fills the header subtitle, resets the dropdown,
// disables confirm, then opens the dialog. Picking a reason enables confirm and
// shows that reason's "when to use" guidance under the field (CONEK-6560); a
// successful Turbo submit closes the modal (the server redirects back to the list,
// whose default query excludes disqualified rows).
export default class extends Controller {
  static targets = ["modal", "subtitle", "confirmButton", "form", "guidance"]

  open(event) {
    const { contactId, contactName, companyName, disqualifyPath } =
      event.currentTarget.dataset

    // A missing path/id is a template/data regression, not a user error — fail
    // loud in dev rather than open a modal that will POST to the wrong place.
    if (!disqualifyPath || !contactId) {
      console.warn(
        "[contacts--persons-disqualify] trigger is missing data-disqualify-path / data-contact-id",
        event.currentTarget,
      )
      return
    }

    this.formTarget.action = disqualifyPath
    this.subtitleTarget.textContent = [contactName, companyName]
      .filter(Boolean)
      .join(" · ")

    // Reset from any previous open: clear the native field, the Tom Select UI,
    // and re-disable confirm so a stale reason can't be re-submitted.
    this.formTarget.reset()
    this.resetReasonSelect()
    this.setConfirmDisabled(true)

    this.modalController?.open()
  }

  reasonChanged() {
    this.setConfirmDisabled(!this.selectedReason)
    this.renderGuidance()
  }

  // CONEK-6560: show the chosen reason's "when to use" guidance under the field.
  // The text rides along on each <option> as data-description (put there by the
  // reason vocabulary via Ui::SelectComponent's extra data-* bag), so the copy
  // stays server-rendered and translated — nothing is hardcoded here.
  renderGuidance() {
    if (!this.hasGuidanceTarget) return

    const select = this.formTarget.querySelector("[name='reason']")
    const option = select?.selectedOptions?.[0]
    this.guidanceTarget.textContent = option?.dataset.description ?? ""
  }

  submitEnd(event) {
    if (event.detail.success) {
      this.modalController?.close()
    }
  }

  get selectedReason() {
    const select = this.formTarget.querySelector("[name='reason']")
    return select && select.value.trim() !== "" ? select.value : null
  }

  setConfirmDisabled(disabled) {
    this.confirmButtonTarget.disabled = disabled
    this.confirmButtonTarget.setAttribute(
      "aria-disabled",
      disabled ? "true" : "false",
    )
  }

  // form.reset() clears the native <select> value but Tom Select keeps its own
  // rendered item, so clear the instance too when it's present. The guidance
  // line is cleared alongside it, or the previous contact's reason text would
  // still be showing under an empty field on the next open.
  resetReasonSelect() {
    const select = this.formTarget.querySelector("[name='reason']")
    if (select && select.tomselect) {
      select.tomselect.clear(true)
    }
    if (this.hasGuidanceTarget) {
      this.guidanceTarget.textContent = ""
    }
  }

  get modalController() {
    return this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
  }
}
